import { createErrorToastFromTradId } from '@components/ui/toaster';
import { formatDate } from '@utils/date.utils';
import { toPng } from 'html-to-image';

import { DateTimeEnum } from '@/types/dateTime.types';

function getVideoElementFromPlayerId(playerId: string): HTMLVideoElement | HTMLCanvasElement {
  const container = document.getElementById(playerId);
  const video = container?.querySelector('video');
  if (video instanceof HTMLVideoElement) {
    return video;
  }

  const canvas = container?.querySelector('canvas');
  if (canvas instanceof HTMLCanvasElement) {
    return canvas;
  }

  createErrorToastFromTradId('error.screenshotError');
  throw new Error(`Element with playerId ${playerId} does not have a capturable video or canvas child`);
}

function takeScreenshotFromPlayer(video: HTMLVideoElement | HTMLCanvasElement, mimeType: string): string {
  if (video instanceof HTMLCanvasElement) {
    return video.toDataURL(mimeType);
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  if (!context) {
    throw new Error('Could not get a 2d context from canvas');
  }

  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL(mimeType);
}

function downloadImage(base64Image: string, fileName: string) {
  const downloadAnchor = document.createElement('a');
  downloadAnchor.href = base64Image;
  downloadAnchor.download = fileName;
  downloadAnchor.click();
}

function downloadScreenshot(screenshotImg: string, name: string, situationDate: string) {
  const date = formatDate(situationDate, DateTimeEnum.DATE_TIME_SECONDS);
  const fileName = `capture_${name}_${date}.png`;
  downloadImage(screenshotImg, fileName);
}

/**
 * @param playerId The DOM element id of the player
 * @param situationDate Date and time of the situation to put on the file name
 * @param cameraName The name of the captured camera to put on the file name
 */
export function takeAndDownloadScreenshotFromPlayer(
  playerId: string,
  situationDate: string | null,
  cameraName?: string,
) {
  const videoPlayerElement = getVideoElementFromPlayerId(playerId);
  const screenshotImg = takeScreenshotFromPlayer(videoPlayerElement, 'image/png');
  downloadScreenshot(screenshotImg, cameraName ?? 'unknownCameraName', situationDate ?? 'unknownDate');
}

function replaceDynamicElementsInClone(clone: HTMLElement, original: HTMLElement): void {
  const originalVideos = Array.from(original.querySelectorAll('video'));
  const clonedVideos = Array.from(clone.querySelectorAll('video'));

  for (let i = 0; i < originalVideos.length; i++) {
    const originalVideo = originalVideos[i];
    const clonedVideo = clonedVideos[i];

    const canvas = document.createElement('canvas');
    canvas.width = originalVideo.videoWidth;
    canvas.height = originalVideo.videoHeight;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(originalVideo, 0, 0, canvas.width, canvas.height);
    }

    const computedStyle = getComputedStyle(originalVideo);
    for (const prop of computedStyle) {
      canvas.style.setProperty(prop, computedStyle.getPropertyValue(prop));
    }
    canvas.style.width = computedStyle.width;
    canvas.style.height = computedStyle.height;

    clonedVideo.parentNode?.replaceChild(canvas, clonedVideo);
  }

  const originalMapboxCanvases: HTMLCanvasElement[] = Array.from(original.querySelectorAll('canvas.mapboxgl-canvas'));
  const clonedMapboxCanvases: HTMLCanvasElement[] = Array.from(clone.querySelectorAll('canvas.mapboxgl-canvas'));

  for (let i = 0; i < originalMapboxCanvases.length; i++) {
    const originalCanvas = originalMapboxCanvases[i];
    const clonedCanvas = clonedMapboxCanvases[i];

    try {
      const dataUrl = originalCanvas.toDataURL();
      const img = document.createElement('img');
      img.src = dataUrl;

      const style = getComputedStyle(originalCanvas);
      for (const prop of style) {
        img.style.setProperty(prop, style.getPropertyValue(prop));
      }
      img.style.width = style.width;
      img.style.height = style.height;

      clonedCanvas.parentNode?.replaceChild(img, clonedCanvas);
    } catch (err) {
      console.warn('Could not snapshot map canvas — maybe tainted by cross-origin tiles:', err);
    }
  }
}

async function elementToPngString(element: HTMLElement = document.body): Promise<string> {
  const clone = element.cloneNode(true) as HTMLElement;
  replaceDynamicElementsInClone(clone, element);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '-1';
  container.appendChild(clone);
  document.body.appendChild(container);

  try {
    const dataUrl = await toPng(clone);
    return dataUrl;
  } catch (error) {
    console.error('Error converting element to PNG:', error);
    throw error;
  } finally {
    document.body.removeChild(container);
  }
}

export async function screenshotWithVideoFramesAndDownload(
  name: string,
  date: string,
  element: HTMLElement = document.body,
) {
  try {
    await document.fonts.ready;
    const dataUrl = await elementToPngString(element);
    downloadScreenshot(dataUrl, name, date);
  } catch (error) {
    createErrorToastFromTradId('error.screenshotError');
    console.error('Screenshot failed:', error);
  }
}

export async function screenshotVideoAt1080pAndDownload(wrapper: HTMLElement, name: string, date: string) {
  const video = wrapper.querySelector('video');

  if (!video) {
    console.error('No <video> element found inside provided wrapper');
    return;
  }

  // Create 1080p canvas (16:9 aspect ratio)
  const canvas = document.createElement('canvas');
  canvas.width = 1920;
  canvas.height = 1080;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('Unable to get canvas context');
    return;
  }

  try {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/png');
    downloadScreenshot(dataUrl, name, date);
  } catch (error) {
    createErrorToastFromTradId('error.screenshotError');
    console.error('Error capturing video screenshot:', error);
  }
}
